/* eslint-disable react-refresh/only-export-components */
// eslint-disable-next-line
import React, { type ReactElement } from "react";

import { configureStore } from "@reduxjs/toolkit";
import {
  render,
  type RenderResult,
  type RenderOptions,
} from "@testing-library/react";
import userEvent from "@testing-library/user-event";
import { createMemoryHistory, type History } from "history";
import { Provider } from "react-redux";
import { MemoryRouter } from "react-router-dom";

import type { HNOppgaveFrontend } from "../types/Resources";

import { getModifiedResourcesForTest } from "@helsenorge/framework-utils/resources";
import LanguageLocales from "@helsenorge/core-utils/constants/languages";
import { oppgaveFrontendProject } from "../types/project-name-constants";
import {
  resourcesApi,
  getResourcesTestHelper as getResources,
} from "../resources";

import defaultResources from "../resources/HN.Oppgave.Frontend.nb-NO.json";
import rootReducer, { type AppStore, type RootState } from "../store/reducer";
interface Options extends RenderOptions {
  initialState?: RootState;
  history?: History;
  route?: string;
  store?: AppStore;
}
interface ReturnOptions extends RenderOptions {
  history: History;
  store: AppStore;
}
export function getResourcesTestHelper(): HNOppgaveFrontend {
  return getModifiedResourcesForTest(defaultResources) as HNOppgaveFrontend;
}

type FetchResourcesArgs = {
  project: string;
  language: LanguageLocales;
  getResources: () => HNOppgaveFrontend;
};

export const fetchResources = ({
  project,
  language,
  getResources,
}: FetchResourcesArgs) =>
  resourcesApi.util.upsertQueryData(
    "getResources",
    { projectName: project, language },
    getResources(),
  );

export const renderWithRouter = (
  ui: ReactElement,
  options?: Options,
): ReturnOptions => {
  const history = createMemoryHistory();
  history.push(options?.route ?? "/");
  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(resourcesApi.middleware),
  });
  const AllTheProvidersWithRouter: React.FC = ({
    children,
  }: {
    children?: React.ReactNode;
  }) => {
    return (
      <Provider store={store}>
        <MemoryRouter>{children}</MemoryRouter>
      </Provider>
    );
  };
  return {
    ...render(ui, { wrapper: AllTheProvidersWithRouter, ...options }),
    history,
    store,
  };
};

export const customRender = (
  ui: ReactElement,
  options?: Options,
): RenderResult => {
  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(resourcesApi.middleware),
  });

  const AllTheProviders: React.FC = ({
    children,
  }: {
    children?: React.ReactNode;
  }) => {
    return <Provider store={store}>{children}</Provider>;
  };
  return render(ui, { wrapper: AllTheProviders, ...options });
};
const user = userEvent.setup();
export * from "@testing-library/react";
export {
  user as userEvent,
  renderWithRouter as render,
  customRender as renderWithoutRouter,
  LanguageLocales,
  oppgaveFrontendProject,
  getResources,
};

// Helper to construct absolute URL for fetch in test environment
export const getAbsoluteUrl = (path: string): string => {
  if (typeof window !== "undefined" && window.location?.href) {
    // Browser environment - return as-is, browser will resolve relative URLs
    return path;
  }
  // Test/Node environment - need absolute URL
  return `http://localhost:3000${path}`;
};
